import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { getPimActivations, PimActivation } from '@/api/adminApi';

export default function PimPage() {
  const [loadingActivations, setLoadingActivations] = useState(true);
  const [activations, setActivations] = useState<PimActivation[]>([]);
  const [activationSearch, setActivationSearch] = useState('');
  const [activationTypeFilter, setActivationTypeFilter] = useState('all');
  const [activationResultFilter, setActivationResultFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadActivations();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activationSearch, activationTypeFilter, activationResultFilter]);

  async function loadActivations() {
    setLoadingActivations(true);
    setError(null);
    try {
      const data = await getPimActivations();
      setActivations(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load PIM activations');
    } finally {
      setLoadingActivations(false);
    }
  }

  const activationTypes = useMemo(() => {
    const types = new Set<string>();
    activations.forEach((a) => {
      if (a.activationType?.trim()) {
        types.add(a.activationType.trim());
      }
    });
    return Array.from(types).sort();
  }, [activations]);

  const activationResults = useMemo(() => {
    const results = new Set<string>();
    activations.forEach((a) => {
      if (a.result?.trim()) {
        results.add(a.result.trim());
      }
    });
    return Array.from(results).sort();
  }, [activations]);

  const filteredActivations = useMemo(() => {
    const q = activationSearch.trim().toLowerCase();

    return activations.filter((a) => {
      if (activationTypeFilter !== 'all' && (a.activationType || '') !== activationTypeFilter) {
        return false;
      }

      if (activationResultFilter !== 'all' && (a.result || '') !== activationResultFilter) {
        return false;
      }

      if (!q) {
        return true;
      }

      const haystack = [
        a.userDisplayName,
        a.userPrincipalName,
        a.role,
        a.targetResource,
        a.reason,
        a.activityDisplayName,
        a.uniqueId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [activations, activationSearch, activationTypeFilter, activationResultFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredActivations.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedActivations = useMemo(
    () => filteredActivations.slice(startIndex, endIndex),
    [filteredActivations, startIndex, endIndex]
  );

  return (
    <div className="container-fluid mt-4">
      <div className="mb-3">
        <h2 className="mb-1">PIM</h2>
        <p className="text-muted mb-0">Review Privileged Identity Management activations.</p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>PIM Activations</strong>
          <Badge bg="secondary">{filteredActivations.length} matches</Badge>
        </Card.Header>
        <Card.Body>
          <Row className="g-2 mb-3">
            <Col md={4}>
              <Form.Control
                type="search"
                placeholder="Search user, role, resource, reason..."
                value={activationSearch}
                onChange={(e) => setActivationSearch(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Select value={activationTypeFilter} onChange={(e) => setActivationTypeFilter(e.target.value)}>
                <option value="all">All activation types</option>
                {activationTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select value={activationResultFilter} onChange={(e) => setActivationResultFilter(e.target.value)}>
                <option value="all">All results</option>
                {activationResults.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2} className="text-end">
              <Button variant="outline-secondary" onClick={loadActivations}>
                Refresh
              </Button>
            </Col>
          </Row>

          {loadingActivations ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : filteredActivations.length === 0 ? (
            <Alert variant="info" className="mb-0">No PIM activations match your current filters.</Alert>
          ) : (
            <>
            <div className="table-responsive">
              <Table hover striped>
                <thead>
                  <tr>
                    <th>Activity Time (UTC)</th>
                    <th>User</th>
                    <th>Role</th>
                    <th>Target Resource</th>
                    <th>Activation Type</th>
                    <th>Result</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedActivations.map((row) => (
                    <tr key={row.pimActivationId}>
                      <td>{row.activityDateTime ? new Date(row.activityDateTime).toLocaleString() : '-'}</td>
                      <td>
                        <div>{row.userDisplayName || '-'}</div>
                        <small className="text-muted">{row.userPrincipalName || ''}</small>
                      </td>
                      <td>{row.role || '-'}</td>
                      <td>{row.targetResource || '-'}</td>
                      <td>{row.activationType || '-'}</td>
                      <td>{row.result || '-'}</td>
                      <td className="text-truncate" style={{ maxWidth: 320 }} title={row.reason || ''}>
                        {row.reason || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
              <div className="text-muted small">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredActivations.length)} of {filteredActivations.length}
              </div>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </Button>
                <div className="d-flex align-items-center px-2">
                  <span className="small">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </Button>
              </div>
            </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

